import {forkJoin} from 'rxjs';
import {Component, EventEmitter, Input, Output, ViewChild} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';
import {CommonModalComponent} from '../../../../shared/common-modal/common-modal.component';
import {Reference} from "../../../../configuration/shared/model/reference";
import {SwitchBoardComponent} from "../../../../configuration/shared/model/component";
import {Range} from "../../../../shared/model/range-model";
import {MessageService} from "primeng/api";
import {ComponentService} from "../../../../configuration/shared/services/component.service";
import {AutoComplete} from "primeng/autocomplete";
import {Observable} from 'rxjs';

export class GcrReferenceAssociation {
  reference: string;
  description: string;
  associateGcr: string;
  associateToCurrentGcr: boolean;
  isDisabled: boolean;


  constructor(reference: string, description: string, associateGcr: string, alreadyAssociate: boolean, isDisabled: boolean) {
    this.reference = reference;
    this.description = description;
    this.associateGcr = associateGcr;
    this.associateToCurrentGcr = alreadyAssociate;
    this.isDisabled = isDisabled;
  }
}

@Component({
  standalone: false,
  selector: 'app-odm-list-gcr-references-modal',
  templateUrl: './odm-associate-gcr-modal.component.html',
  styleUrls: ['./odm-associate-gcr-modal.component.less'],
})
export class OdmAssociateGCRModalComponent {

  @ViewChild('associateGCRModal', {static: false}) associateGCRModal: CommonModalComponent;
  @ViewChild('deleteAssociateGcrModal') deleteAssociateGcrModal: CommonModalComponent;
  @ViewChild('editAssociateGcrModal') editAssociateGcrModal: CommonModalComponent;
  @ViewChild('confirmationGcrModal') confirmationGcrModal: CommonModalComponent;
  @ViewChild('autoCompleteInput') autoCompleteInput!: AutoComplete;
  @ViewChild('dt') dt: any;

  @Input()
  references: Reference[];
  @Input()
  selectedRange: Range;

  gcrValue: any;
  searchTerm: string = '';
  currentGcr: string = '';
  referencesList: GcrReferenceAssociation[] = [];
  components: SwitchBoardComponent[];
  componentsToUpdate: SwitchBoardComponent[] = [];
  filteredAssociateGcrReferenceList: string[] = [];

  originalGcrList: string[] = [];
  isSelectAllChecked = false;
  editGcrError: string = '';

  @Output()
  onClose = new EventEmitter();

  constructor(private readonly translateService: TranslateService,
              private readonly messageService: MessageService,
              private readonly componentService: ComponentService) {
  }

  show(): void {
    this.currentGcr = '';
    this.clear();
    this.initLists();
    this.resetCheckboxSelection();
  }


  getReferenceList() {
    // get the list of key of originalGcrList
    this.referencesList = this.components.map(component =>
      new GcrReferenceAssociation(
        component.reference.ref,
        component.reference.description,
        component.gcr,
        component.gcr === this.currentGcr && this.currentGcr !== null && this.currentGcr !== '',
        this.shouldDisableReference(component)))
      .sort((a, b) => (a.reference.toUpperCase() > b.reference.toUpperCase() ? 1 : -1));
  }

  private shouldDisableReference(component: SwitchBoardComponent): boolean {
    const currentGcrTrimmed = this.currentGcr?.trim();
    const componentGcrTrimmed = component.gcr?.trim();
    if (!currentGcrTrimmed && componentGcrTrimmed) {
      return true;
    }
    return currentGcrTrimmed !== componentGcrTrimmed && !!componentGcrTrimmed;
  }

  hide() {
    this.resetCheckboxSelection();
    this.onClose.emit();
    this.associateGCRModal.hide();
  }

  clear() {
    this.dt.clear();
    this.searchTerm = ''
  }

  onSearchInputChange(event: any) {
    this.dt.filterGlobal(event.target.value, 'contains');
    this.resetSelection();
  }

  isAllSelected(): boolean {
    if (this.dt === null || this.dt === undefined || this.dt.filteredValue === null || this.dt.filteredValue === undefined) {
      return this.referencesList
        .filter(ref => !ref.isDisabled)          // ignore disabled references
        .every(ref => ref.associateToCurrentGcr === true);
    }
    return this.dt.filteredValue
      .filter((ref: GcrReferenceAssociation) => !ref.isDisabled)
      .every((ref: GcrReferenceAssociation) => ref.associateToCurrentGcr === true);
  }

  toggleSelectAll(): void {
    const isAllSelected = this.isAllSelected();
    this.componentsToUpdate = [];
    if (this.dt === null || this.dt === undefined || this.dt.filteredValue === null || this.dt.filteredValue === undefined) {
      this.referencesList.forEach(ref => {
        this.onSelectAllReferencesAction(ref, isAllSelected);
      });
    } else {
      this.dt.filteredValue.forEach((ref: GcrReferenceAssociation) => {
        this.onSelectAllReferencesAction(ref, isAllSelected);
      });
    }
  }

  private onSelectAllReferencesAction(ref: GcrReferenceAssociation, isAllSelected: boolean) {
    if (!ref.isDisabled) {
      ref.associateToCurrentGcr = !isAllSelected;
      if (ref.associateToCurrentGcr || (isAllSelected && this.currentGcr !== '')) {
        this.addComponentsToUpdate(ref);
      }
    }
  }

  /**
   * Select GCR reference from autocomplete
   * @param event
   */
  onChangesGcrField(event: any): void {
    this.currentGcr = event;
    if (this.isExistingGcr()) {
      this.getReferenceList();
    } else {
      this.resetSelection();
    }

  }


  public isExistingGcr(): boolean {
    return this.currentGcr && this.originalGcrList.includes(this.currentGcr);
  }

  public containsInvalidGcrCharacters(value: string): boolean {
    return /[\\/]/.test(value);
  }

  onAssociateGCRChange(ref: any): void {
    this.addComponentsToUpdate(ref);
  }


  onClickEditAssociateGCRButton(): void {
    this.editGcrError = '';
    this.handleGcrAction(this.editAssociateGcrModal);
  }

  isValid(): boolean {
    return this.currentGcr !== null && this.currentGcr !== '' && !this.containsInvalidGcrCharacters(this.currentGcr);
  }


  /**
   * Confirm edit GCR name for references
   */
  confirmEdit(): void {
    if (this.containsInvalidGcrCharacters(this.gcrValue)) {
      this.editGcrError = this.translateService.instant('T_GCR_NAME_INVALID_CHARACTERS');
      return;
    }

    this.editGcrError = '';
    if (this.gcrValue) {
      this.currentGcr = this.gcrValue;
      this.referencesList.forEach(ref => {
        if (ref.associateToCurrentGcr) {
          ref.associateGcr = this.gcrValue;
          this.addComponentsToUpdate(ref);
        }
      });
    }
    this.saveOrUpdateGcr();
  }

  onGcrValueChange(value: string): void {
    if (this.containsInvalidGcrCharacters(value)) {
      this.editGcrError =
        this.translateService.instant('T_GCR_NAME_INVALID_CHARACTERS');
    } else {
      this.editGcrError = '';
    }
  }
  /**
   * Close edit modal
   */
  closeEditModal(): void {
    this.editAssociateGcrModal.hide();
  }

  /**
   * Open delete confirmation modal
   */
  onClickDeleteConfirmationModal(): void {
    this.handleGcrAction(this.deleteAssociateGcrModal);
  }

  /**
   * Confirm delete GCR name from references
   */
  confirmDelete(): void {
    this.currentGcr = '';
    this.referencesList.forEach(ref => {
      if (ref.associateToCurrentGcr) {
        ref.associateGcr = '';
        ref.associateToCurrentGcr = false;
        this.addComponentsToUpdate(ref);
      }
    });
    this.updateRefsAndSave('T_SUCCESSFULLY_DELETED_GCR', 'T_FAILED_TO_DELETE_GCR');
  }

  /**
   * Close delete modal
   */
  closeDeleteModal(): void  {
    this.deleteAssociateGcrModal.hide();
  }

  /**
   * Handle GCR action for edit/delete
   * @param modal
   * @private
   */
  private handleGcrAction(modal: CommonModalComponent): void {
    if (this.isExistingGcr()) {
      this.gcrValue = this.currentGcr;
      modal.show();
    }
  }
  /**
   * Save GCR with references
   */
  saveOrUpdateGcr(): void {
    const successMessageKey = this.referencesList.some(ref => ref.associateGcr === this.currentGcr)
      ? 'T_SUCCESSFULLY_UPDATED_GCR'
      : 'T_SUCCESSFULLY_CREATE_GCR';
    const errorMessageKey = this.referencesList.some(ref => ref.associateGcr === this.currentGcr)
      ? 'T_FAILED_TO_UPDATE_GCR'
      : 'T_FAILED_TO_CREATE_GCR';
    this.componentService.updateComponents(this.componentsToUpdate)
      .subscribe({
        next: () => {
          this.showSuccessMessage(successMessageKey);
          this.initLists();
        },
        error: () => {
          this.showErrorMessage(errorMessageKey);
        }
      });
  }

  filterAssociateGcrReference(event: any): void {
    const query = event.query.trim().toLowerCase();
    // Filter GCR values from the originalGcrList based on the query
    this.filteredAssociateGcrReferenceList = this.originalGcrList
      .filter(gcr => gcr && gcr.toLowerCase().includes(query));
  }

  private initLists() {
    forkJoin([
      this.componentService.getPublishedComponentsByRangeId(this.selectedRange.id),
      this.componentService.getGcrList(this.selectedRange.id)
    ]).subscribe(results => {
      this.components = results[0];
        this.originalGcrList = Object.values(results[1]).sort((a, b) =>  (a.toUpperCase() > b.toUpperCase() ? 1 : -1));
      this.getReferenceList();
      this.associateGCRModal.show();
    });
    this.componentsToUpdate = [];
  }

  private addComponentsToUpdate(ref: GcrReferenceAssociation) {
    const component = this.components.find(component => component.reference.ref === ref.reference);
    if (!component) {
      return;
    }
    component.gcr = ref.associateToCurrentGcr ? this.currentGcr : '';
    this.componentsToUpdate = this.componentsToUpdate
      .filter(comp => comp.reference.ref !== ref.reference);
    this.componentsToUpdate.push(component);
  }

  private resetSelection() {
    if (!this.currentGcr.trim()) {
      this.referencesList.forEach(ref => {
        const hasAssociateGcr = ref.associateGcr?.trim() !== '';
        ref.associateToCurrentGcr = false;
        ref.isDisabled = hasAssociateGcr; // Disable if associateGcr is set, enable otherwise
      });
    } else {
      this.initLists();
    }
    this.componentsToUpdate = [];
  }

  private updateRefsAndSave(successKey: string, errorKey: string): void {
    this.componentService.updateComponents(this.componentsToUpdate).subscribe({
      next: () => {
        this.showSuccessMessage(successKey);
        this.initLists();
      },
      error: () => this.showErrorMessage(errorKey)
    });
  }

  private showSuccessMessage(messageKey: string): { severity: string; summary: string; detail: string } {
    const message = {
      severity: 'success',
      summary: this.translateService.instant('T_SUCCESS'),
      detail: this.translateService.instant(messageKey),
    };
    this.messageService.add(message);
    return message;
  }

  private showErrorMessage(messageKey: string): { severity: string; summary: string; detail: string } {
    const message = {
      severity: 'error',
      summary: this.translateService.instant('T_ERROR'),
      detail: this.translateService.instant(messageKey),
    };
    this.messageService.add(message);
    return message;
  }

  resetCheckboxSelection(): void {
    this.isSelectAllChecked = false;
  }

}
